import React, { Suspense, useRef, useState } from "react";
import * as THREE from "three";
import { Link } from "react-router-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  Float,
  PerspectiveCamera,
  Environment,
  MeshTransmissionMaterial,
  ContactShadows,
  Text,
  useScroll as useThreeScroll,
} from "@react-three/drei";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  AnimatePresence,
} from "framer-motion";
import "../UIcss/UIHome.css";
import {
  FaCopy,
  FaCheck,
  FaRocket,
  FaCode,
  FaRobot,
  FaMagic,
  FaLayerGroup,
  FaBolt,
} from "react-icons/fa";

// --- 3D FLOATING COMPONENTS ---
const FloatingUI = ({ position, rotation, color, type }) => {
  const mesh = useRef();
  useFrame((state) => {
    if (!mesh.current) return;
    mesh.current.rotation.z += 0.002;
    mesh.current.rotation.x += 0.001;
  });

  return (
    <Float speed={1.5} rotationIntensity={0.5} floatIntensity={1}>
      <mesh position={position} rotation={rotation} ref={mesh}>
        {type === "button" && <boxGeometry args={[2, 0.8, 0.1]} />}
        {type === "alert" && <boxGeometry args={[2.5, 1.2, 0.1]} />}
        {type === "circle" && <cylinderGeometry args={[0.5, 0.5, 0.1, 32]} />}
        <meshPhysicalMaterial
          transmission={1}
          thickness={0.5}
          roughness={0.1}
          ior={1.5}
          color={color}
          transparent
          opacity={0.6}
        />
      </mesh>
    </Float>
  );
};

const CinematicScene = () => (
  <Canvas shadows={{ type: THREE.PCFShadowMap }} dpr={[1, 2]}>
    <PerspectiveCamera makeDefault position={[0, 0, 10]} fov={50} />
    <ambientLight intensity={0.5} />
    <spotLight
      position={[10, 10, 10]}
      angle={0.15}
      penumbra={1}
      intensity={2}
      castShadow
    />
    <Environment preset="city" />

    <Suspense fallback={null}>
      {/* Central Hero Components */}
      <FloatingUI position={[-4, 2, 2]} rotation={[0.4, 0.2, 0]} color="#00f3ff" type="button" />
      <FloatingUI position={[5, -1, 3]} rotation={[-0.2, -0.4, 0.2]} color="#bc13fe" type="alert" />
      <FloatingUI position={[-3, -3, 4]} rotation={[0.1, 0.5, -0.1]} color="#ffd700" type="circle" />
      <FloatingUI position={[4, 3, 1]} rotation={[0.5, -0.2, 0.4]} color="#00f3ff" type="circle" />

      {/* Background Field */}
      <FloatingUI position={[-8, 5, -5]} rotation={[0, 0, 0]} color="#00f3ff" type="circle" />
      <FloatingUI position={[10, -3, -8]} rotation={[0.5, 0.5, 0.5]} color="#bc13fe" type="button" />
      <FloatingUI position={[-12, -5, -10]} rotation={[1, 0, 1]} color="#ffd700" type="alert" />
      <FloatingUI position={[7, 7, -12]} rotation={[0, 1, 0]} color="#00f3ff" type="circle" />
      <FloatingUI position={[-5, -8, -15]} rotation={[0.3, 0.3, 0.3]} color="#bc13fe" type="button" />
      <FloatingUI position={[15, 2, -20]} rotation={[0, 0, 1]} color="#00f3ff" type="alert" />
      <FloatingUI position={[-15, 8, -18]} rotation={[0.8, 0.2, 0.4]} color="#ffd700" type="circle" />
      <FloatingUI position={[-20, 0, -25]} rotation={[0.5, 0.5, 0.5]} color="#00f3ff" type="alert" />
      <FloatingUI position={[18, -8, -22]} rotation={[0.1, 0.1, 0.8]} color="#ffd700" type="circle" />

      <ContactShadows position={[0, -4.5, 0]} scale={20} blur={2} far={4.5} />
    </Suspense>
  </Canvas>
);

// --- PREVIEW CARD COMPONENT ---
const ComponentPreviewCard = ({ title, icon, points, delay }) => {
  const [copied, setCopied] = useState(false);

  return (
    <motion.div
      className="cinematic-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.8, ease: "easeOut" }}
    >
      <div className="card-hologram"></div>
      <div className="card-header">
        <span className="card-icon">{icon}</span>
        <h3>{title}</h3>
      </div>
      
      <ul className="card-points">
        {points && points.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>

      <div className="card-footer">
        <button className="explore-cta">
          EXPLORE <FaRocket style={{ marginLeft: '8px' }} />
        </button>
      </div>
    </motion.div>
  );
};

const UIHome = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });

  const smoothY = useSpring(scrollYProgress, { stiffness: 50, damping: 20 });
  const heroOpacity = useTransform(smoothY, [0, 0.2], [1, 0]);
  const heroScale = useTransform(smoothY, [0, 0.2], [1, 0.8]);
  const bgColor = useTransform(
    smoothY,
    [0, 0.5, 1],
    ["#020205", "#0a0a1a", "#020205"],
  );

  return (
    <motion.div
      className="cinematic-universe"
      ref={containerRef}
      style={{ backgroundColor: bgColor }}
    >
      <div className="three-background-layer">
        <CinematicScene />
      </div>

      <div className="left-sidebar-hud">
        <div className="hud-metric">
          <span className="m-label">SYSTEM</span>
          <span className="m-value">ACTIVE</span>
          <div className="m-bar"><div className="m-fill"></div></div>
        </div>
        <div className="hud-metric">
          <span className="m-label">CORE-V</span>
          <span className="m-value">1.0.4</span>
        </div>
        <div className="hud-vertical-text">CODEPULSE // INTERFACE LAYER</div>
      </div>



      {/* SCENE 1: THE VISION */}
      <section className="scene hero-scene">
        <motion.div
          className="hero-wrap"
          style={{ opacity: heroOpacity, scale: heroScale }}
        >
          <div className="pre-title">CODEPULSE-R SYSTEMS</div>
          <h1 className="cinematic-title">
            <span className="glow-text">FUTURE</span>
            <br />
            <span className="outline-text">INTERFACE</span>
          </h1>
          <p className="hero-sub">
            The <span className="highlight-yellow">next evolution</span> in developer experience. <span className="highlight-yellow">Immersive</span>,
            <span className="highlight-yellow">high-performance</span>, and <span className="highlight-yellow">copy-paste ready</span> components for the modern
            web.
          </p>
          <div className="hero-btns">
            <button className="btn-apple-primary">EXPLORE CORE</button>
            <button className="btn-apple-secondary">VIEW SOURCE</button>
          </div>
        </motion.div>
      </section>

      {/* SCENE 2: THE ECOSYSTEM */}
      <section className="scene gallery-scene">
        <div className="scene-header">
          <h2 className="scene-title">The Component Ecosystem</h2>
        </div>

        <div className="cinematic-grid">
          {[
            { 
              title: "Alert Systems", 
              icon: <FaBolt />, 
              points: ["Real-time notifications", "Cyber-style glass", "Animated status", "Multi-type alerts", "Sound support"]
            },
            { 
              title: "Core Buttons", 
              icon: <FaRocket />, 
              points: ["Premium glassmorphism", "Interactive hover", "Haptic feedback", "Multi-state logic", "Ripple effects"]
            },
            { 
              title: "Holo Navbars", 
              icon: <FaLayerGroup />, 
              points: ["Cinematic blur", "Floating design", "Dynamic active state", "Responsive layout", "Holo borders"]
            },
            { 
              title: "Smart Inputs", 
              icon: <FaCode />, 
              points: ["Advanced validation", "Custom placeholders", "Glass focus state", "Multi-line support", "Smooth transitions"]
            },
            { 
              title: "3D Modals", 
              icon: <FaLayerGroup />, 
              points: ["Immersive depth", "Spring animations", "Backdrop blur", "Focus trapping", "Content isolation"]
            },
            { 
              title: "Holo Icons", 
              icon: <FaMagic />, 
              points: ["Vector precision", "Glow integration", "Hover physics", "Dynamic coloring", "Modern silhouettes"]
            },
            { 
              title: "System Spinners", 
              icon: <FaBolt />, 
              points: ["Zero-latency feel", "Smooth rotations", "SVG optimization", "Multi-variant styles", "Performance focused"]
            },
            { 
              title: "Data Tables", 
              icon: <FaLayerGroup />, 
              points: ["High-density rows", "Sorting logic", "Glass filtering", "Responsive cells", "Export ready"]
            },
            { 
              title: "Type Headings", 
              icon: <FaCode />, 
              points: ["Modern typography", "Gradient text", "Precise tracking", "Scale responsive", "Multi-weight support"]
            },
            { 
              title: "Visual Colors", 
              icon: <FaMagic />, 
              points: ["Harmonic palettes", "CSS variable driven", "Dark mode ready", "High contrast", "Branding focused"]
            },
            { 
              title: "Logical Entity", 
              icon: <FaLayerGroup />, 
              points: ["Object mapping", "State management", "Data persistence", "Recursive rendering", "Optimized perf"]
            },
            { 
              title: "Code Viewers", 
              icon: <FaCode />, 
              points: ["Syntax highlighting", "One-click copy", "Line numbering", "Multiple themes", "Language detection"]
            },
          ].map((item, index) => (
            <ComponentPreviewCard
              key={index}
              title={item.title}
              icon={item.icon}
              points={item.points}
              delay={index * 0.05}
            />
          ))}
        </div>
      </section>

      {/* SCENE 3: THE FORGE (COMPILER) */}
      <section className="scene forge-scene">
        <div className="forge-glass-container">
          <div className="forge-info">
            <h2>Quantum Execution</h2>
            <p>
              Integrated cloud-based compilers for Java, SQL, and React.
              Real-time validation with zero-latency preview.
            </p>
            <div className="forge-stats-apple">
              <div className="f-stat-card">
                <span className="s-label">Latency</span>
                <span className="s-val">120ms</span>
              </div>
              <div className="f-stat-card">
                <span className="s-label">Uptime</span>
                <span className="s-val">99.9%</span>
              </div>
            </div>
          </div>
          <div className="forge-visual-apple">
            <div className="cyber-terminal">
              <div className="terminal-header">
                <div className="t-dot red"></div>
                <div className="t-dot yellow"></div>
                <div className="t-dot green"></div>
              </div>
              <div className="terminal-body">
                <p className="t-line">Compiling modules...</p>
                <p className="t-line highlight"> Optimization complete</p>
                <p className="t-line"> System ready</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SCENE 4: FINAL CALL */}
      <section className="scene final-scene">
        <motion.div
          className="final-cta-wrap"
          whileInView={{ scale: [0.9, 1], opacity: [0, 1] }}
        >
          <FaRobot className="final-icon" />
          <h2>Join the Evolution</h2>
          <p>Deploy futuristic interfaces in minutes.</p>
          <button className="btn-launch-final">INITIALIZE NOW</button>
        </motion.div>
      </section>
    </motion.div>
  );
};

export default UIHome;
